import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cn } from 'cn';
import { Spinner } from '@/components/ui/spinner';
import { closeHelpChat } from '../../redux/slices/helpChatSlice';

export const GUIDE_URL = process.env.REACT_APP_GUIDE_URL || 'https://docs.bridged.events';
const GUIDE_ORIGIN = new URL(GUIDE_URL).origin;

/**
 * Help → Ask AI: the Bridged user guide's own chat (`?panel=ask` on docs.bridged.events) in a side panel. The guide
 * draws the whole chat, its header and close button included; this panel holds it, tells it the theme and closes
 * when it sends `guide:close`.
 *
 * Unlike a Sheet it stays mounted once opened (inert while hidden), so closing and reopening keeps the conversation.
 * It doesn't block the page, so people can follow the steps while they read them.
 */
function GlobalHelpChat() {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.helpChat.isOpen);
  const isDarkTheme = useSelector(state => state.app.isDarkTheme);
  const frame = useRef(null);
  const [opened, setOpened] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // The theme it starts in; later changes go by message so the chat isn't reloaded.
  const [src] = useState(() => `${GUIDE_URL}/?panel=ask&theme=${isDarkTheme ? 'dark' : 'light'}`);

  useEffect(() => {
    if (isOpen) setOpened(true);
  }, [isOpen]);

  useEffect(() => {
    const onMessage = event => {
      if (event.origin !== GUIDE_ORIGIN || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'guide:close') dispatch(closeHelpChat());
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dispatch]);

  useEffect(() => {
    frame.current?.contentWindow?.postMessage(
      { type: 'guide:theme', theme: isDarkTheme ? 'dark' : 'light' },
      GUIDE_ORIGIN
    );
  }, [isDarkTheme, loaded]);

  // Opening puts the cursor in the question box; Escape outside the chat closes it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const focus = () => {
      frame.current?.focus();
      frame.current?.contentWindow?.postMessage({ type: 'guide:focus' }, GUIDE_ORIGIN);
    };
    // after the Help menu has closed and handed focus back to its button
    const timer = loaded ? setTimeout(focus, 60) : undefined;
    const onKey = event => {
      if (event.key === 'Escape' && !event.defaultPrevented) dispatch(closeHelpChat());
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, loaded, dispatch]);

  if (!opened) return null;

  return (
    <aside
      role="dialog"
      aria-label="Ask AI"
      aria-hidden={!isOpen}
      inert={!isOpen}
      className={cn(
        'fixed inset-y-0 end-0 z-50 flex w-full flex-col border-s bg-popover shadow-xl sm:max-w-[460px]',
        'transition-[translate,visibility] ease-drawer motion-reduce:transition-none',
        isOpen
          ? 'visible translate-x-0 duration-400'
          : 'invisible translate-x-full duration-250 rtl:-translate-x-full'
      )}
    >
      {!loaded && (
        <div className="absolute inset-0 grid place-items-center text-muted-foreground">
          <Spinner className="size-5" />
        </div>
      )}
      <iframe
        ref={frame}
        src={src}
        title="Ask AI: answers from the Bridged user guide"
        onLoad={() => setLoaded(true)}
        className={cn('size-full border-0 bg-transparent', !loaded && 'opacity-0')}
      />
    </aside>
  );
}

export default GlobalHelpChat;
